import { useCallback, useMemo, useState } from 'react';
import { useObservableValue } from '../../hooks';
import { useSocketPerformance } from '../../providers';
import { AccordionGroup } from '../Accordion';
import { Button } from '../Button';
import { Box, HStack, VStack } from '../Core';
import { FilterInput } from '../FilterInput';
import { TextArea, Toggle } from '../Form';
import { Tab, TabList, TabPanel, TabSize, Tabs, useTabs } from '../Tabs';
import { ErroredStreamsBlotter } from './ErroredStreamsBlotter';
import { StreamStatisticsBlotter } from './StreamDiagnosticsBlotter';
import { DataTextArea } from './styles';

export type StreamDisagnosticsTab = 'active' | 'canceled' | 'errored';

export const TAB_TO_INDEX = {
  active: 0 as const,
  canceled: 1 as const,
  errored: 2 as const,
} satisfies Record<StreamDisagnosticsTab, number>;

interface StreamDiagnosticsDialogProps {
  tab: StreamDisagnosticsTab;
  onReload?: () => void;
}

export default function StreamDiagnosticsDialog({ tab, onReload }: StreamDiagnosticsDialogProps) {
  const performance = useSocketPerformance();
  const [shownMessages, setShownMessages] = useState<{ reqid: string; messages: string }>();
  const [filterText, setFilterText] = useState('');

  const tabItems = useMemo(() => {
    return [
      {
        label: 'Active Subscriptions',
        render: () => (
          <StreamStatisticsBlotter
            activeSubscriptions={true}
            onShowMessages={setShownMessages}
            filterText={filterText}
          />
        ),
      },
      {
        label: 'Cancelled Subscriptions',
        render: () => (
          <StreamStatisticsBlotter
            activeSubscriptions={false}
            onShowMessages={setShownMessages}
            filterText={filterText}
          />
        ),
      },
      {
        label: 'Errored Subscriptions',
        render: () => <ErroredStreamsBlotter onShowMessages={setShownMessages} filterText={filterText} />,
      },
    ];
  }, [filterText]);

  const tabs = useTabs({
    initialSelectedIndex: TAB_TO_INDEX[tab],
    initialItems: tabItems,
  });

  const isEnabled = useObservableValue<boolean>(
    () => performance.isLogSubscriptionInfo$,
    [performance.isLogSubscriptionInfo$]
  );

  const onEnabledChange = useCallback(
    (enabled: boolean) => {
      performance.setLogSubscriptionInfo(enabled);
      if (enabled) {
        (onReload ?? (() => window.location.reload()))();
      }
    },
    [performance, onReload]
  );

  return (
    <Tabs {...tabs} size={TabSize.Large} onClick={() => setShownMessages(undefined)}>
      <TabList
        isBordered={true}
        rightItems={
          <HStack gap={10}>
            <FilterInput onChange={setFilterText} value={filterText} tooltip="Filter by subscription" />
            <Toggle checked={!!isEnabled} onChange={onEnabledChange} label={isEnabled ? 'Enabled' : 'Disabled'} />
          </HStack>
        }
      >
        {tabItems.map((tab, index) => (
          <Tab key={`${tab.label}-${index}`} label={tab.label} />
        ))}
      </TabList>
      {tabItems.map((tab, index) => {
        const hidden = tabs.selectedIndex !== index;
        return (
          <TabPanel hidden={hidden} key={`${tab.label}-${index}-${isEnabled}`} display="flex" flexDirection="column">
            <AccordionGroup>
              {!hidden && (
                <HStack>
                  {isEnabled ? (
                    tab.render()
                  ) : (
                    <Box h="70vh" w="1200px">
                      <VStack h="100%" gap="spacing8">
                        <Button onClick={() => onEnabledChange(true)}>Start Diagnostics</Button>
                        <div>(will refresh page)</div>
                      </VStack>
                    </Box>
                  )}
                  {shownMessages && (
                    <DataTextArea key={shownMessages.reqid}>
                      <TextArea
                        width={580}
                        height="70vh"
                        onClick={evt => evt.stopPropagation()}
                        defaultValue={shownMessages.messages}
                      />
                    </DataTextArea>
                  )}
                </HStack>
              )}
            </AccordionGroup>
          </TabPanel>
        );
      })}
    </Tabs>
  );
}
